'use client';

import { Icon } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import { cn } from '@workspace/ui/lib/utils';
import { useRouter } from 'next/dist/client/components/navigation';
import { RatingStats } from './RatingStats';

type Props = {
  id: number;
  title: string;
  shortDescription: string;
  coverImageUrl?: string;
  price: number | null;
  slug: string;
  systems: string[];
  rating: {
    average: number;
    total: number;
  };
  isSmall?: boolean;
  setIsChosen?: (value: number | null) => void;
  isChosen?: number | null;
  children?: React.ReactNode;
  isBit?: boolean;
};

export const ProjectCard = ({
  id,
  title,
  shortDescription,
  price,
  slug,
  systems,
  rating,
  coverImageUrl,
  isSmall,
  setIsChosen,
  isChosen,
  children,
  isBit,
}: Props) => {
  const router = useRouter();
  const isLoading = useBoolean(false);

  const handleCardClick = (
    e: React.MouseEvent<HTMLDivElement>,
    id?: number
  ) => {
    if (isBit) {
      e.stopPropagation();
      return;
    }
    if (!isSmall) {
      isLoading.onTrue();
      router.push(`/project/${slug}`);
    } else {
      setIsChosen?.(id as number);
    }
  };

  return (
    <div
      className={cn(
        'relative group flex flex-col gap-2 rounded-[20px] cursor-pointer md:w-52 md:min-h-[340px] w-72 transition-all duration-200 p-2.5 justify-between',
        {
          'hover:shadow-lg': !isBit,
          'bg-white': isSmall,
          'border-1 border-blue-primary': isSmall && isChosen === id,
        }
      )}
      onClick={(e) => handleCardClick(e, id)}
    >
      {children}
      <div className="relative rounded-[20px] overflow-hidden ">
        <img
          src={coverImageUrl}
          alt={title}
          className={cn('w-full h-auto transition-transform duration-300', {
            'group-hover:scale-[1.03]': !isBit,
          })}
        />
        <div
          className={cn(
            'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 bg-gradient-to-t from-black/20 via-black/5 to-transparent',
            {
              'group-hover:opacity-100': !isBit,
            }
          )}
        />
      </div>

      <div className="flex flex-col gap-1">
        <h3 className="text-lg! font-semibold! line-clamp-1 break-all">
          {title}
        </h3>
        <span className="text-xs text-black/80 font-light">
          {shortDescription}
        </span>
        <span className="text-lg text-black/80 font-semibold">
          {price ? `$${price}` : 'Free'}
        </span>
      </div>
      <div className="flex items-end justify-between text-sm text-black/60">
        <RatingStats
          average={rating?.average ?? 0}
          total={rating?.total ?? 0}
        />
        <span className="flex gap-1 items-center">
          {Array.isArray(systems)
            ? systems.map((system, index) => (
                <Icon
                  key={index}
                  name={system.toLowerCase()}
                  className="size-4 text-black/40"
                />
              ))
            : null}
        </span>
      </div>
    </div>
  );
};
